<template>
    <q-layout view="hHh lpR fFf">
        <marketing-nav :mobile-nav-open="mobileNav" @toggle-mobile="mobileNav = !mobileNav" />

        <q-page-container>
            <article v-if="post" v-intersection="revealCfg" class="news-detail reveal-fade-up">
                <!-- Breadcrumb -->
                <div class="news-detail__crumbs mkt-container">
                    <a href="/">Home</a>
                    <span aria-hidden="true">›</span>
                    <a href="/news">News</a>
                    <span aria-hidden="true">›</span>
                    <span class="news-detail__crumbs-current">{{ post.title }}</span>
                </div>

                <!-- Header -->
                <header class="news-detail__header mkt-container">
                    <h1>{{ post.title }}</h1>
                    <div class="news-detail__meta">
                        <span v-if="post.publishedDate">{{ formatDate(post.publishedDate) }}</span>
                        <span v-if="post.publishedDate &amp;&amp; post.author">·</span>
                        <span v-if="post.author">By {{ post.author }}</span>
                        <span v-if="readingTime">·</span>
                        <span v-if="readingTime">{{ readingTime }} min read</span>
                    </div>
                    <div v-if="postTags.length" class="news-detail__tags">
                        <a v-for="tag in postTags"
                           :key="tag"
                           :href="`/news/tag/${tagSlug(tag)}`"
                           class="news-detail__tag">{{ tag }}</a>
                    </div>
                </header>

                <!-- Top-full hero image -->
                <figure v-if="post.featuredImage &amp;&amp; isTopFull"
                        class="news-detail__hero mkt-container">
                    <img :src="post.featuredImage" :alt="post.title" loading="eager" />
                    <figcaption v-if="post.imageCaption">{{ post.imageCaption }}</figcaption>
                </figure>

                <!-- Body -->
                <div class="news-detail__body mkt-container" :class="bodyClass">
                    <figure v-if="post.featuredImage &amp;&amp; isInlineImage"
                            class="news-detail__inline-fig"
                            :class="inlineFigClass">
                        <img :src="post.featuredImage" :alt="post.title" loading="lazy" />
                        <figcaption v-if="post.imageCaption">{{ post.imageCaption }}</figcaption>
                    </figure>

                    <!-- The HTML content from QEditor. We trust it because it's
                         authored only by eSign Super Admins. -->
                    <div class="news-detail__content" v-html="post.content"></div>
                </div>

                <!-- Bottom-full image -->
                <figure v-if="post.featuredImage &amp;&amp; isBottomFull"
                        class="news-detail__hero mkt-container news-detail__hero--bottom">
                    <img :src="post.featuredImage" :alt="post.title" loading="lazy" />
                    <figcaption v-if="post.imageCaption">{{ post.imageCaption }}</figcaption>
                </figure>

                <!-- Share + back -->
                <div class="news-detail__actions mkt-container">
                    <a href="/news" class="news-detail__back">
                        <q-icon name="arrow_back" size="14px" /> All news
                    </a>
                    <div class="news-detail__share">
                        <span style="font-size:0.8rem;color:#64748b;margin-right:8px;">Share:</span>
                        <a :href="shareTwitter" target="_blank" rel="noopener" aria-label="Share on Twitter/X" class="news-detail__share-icon">
                            <!-- Inline SVG (X / Twitter brand mark) avoids pulling in the entire FontAwesome
                                 webfont (~150 KiB across 6 files) for a single icon. -->
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="16" height="16" aria-hidden="true" focusable="false">
                                <path fill="currentColor" d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8l164.9-188.5L26.8 48h145.6l100.5 132.9L389.2 48zm-24.8 373.8h39.1L151.1 88h-42l255.3 333.8z"/>
                            </svg>
                        </a>
                        <a :href="shareLinkedIn" target="_blank" rel="noopener" aria-label="Share on LinkedIn" class="news-detail__share-icon">
                            <!-- Inline SVG (LinkedIn brand mark). Same rationale as the X icon above. -->
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" aria-hidden="true" focusable="false">
                                <path fill="currentColor" d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z"/>
                            </svg>
                        </a>
                    </div>
                </div>
            </article>

            <div v-else class="text-center q-py-xl">
                <q-icon name="error_outline" size="48px" color="grey-5" />
                <div class="text-h6 q-mt-md text-grey-7">Article not found.</div>
                <a href="/news" class="text-primary text-no-decoration q-mt-md inline-block">View all news</a>
            </div>

            <marketing-footer />
        </q-page-container>

        <!-- Lightbox -->
        <q-dialog v-model="lightboxOpen">
            <q-card class="news-lightbox">
                <img v-if="lightboxSrc" :src="lightboxSrc" :alt="post?.title || ''" />
                <q-btn flat dense round icon="close" v-close-popup class="news-lightbox__close" />
            </q-card>
        </q-dialog>
    </q-layout>
</template>

<script>
import MarketingNav from '@components/marketing/MarketingNav.vue';
import MarketingFooter from '@components/marketing/MarketingFooter.vue';
import { revealMixin } from '@util/reveal.js';

export default {
    name: 'NewsDetail',
    mixins: [revealMixin],
    components: { MarketingNav, MarketingFooter },
    data() {
        return {
            mobileNav: false,
            post: window._contentPost || null,
            lightboxOpen: false,
            lightboxSrc: null
        };
    },
    computed: {
        postTags() {
            if (!this.post?.tags) return [];
            return this.post.tags.split(',').map(t => t.trim()).filter(Boolean);
        },
        readingTime() {
            if (!this.post?.content) return null;
            // Strip HTML tags then count words. ~225 wpm for typical readers.
            const text = this.post.content.replace(/<[^>]+>/g, ' ');
            const words = text.split(/\s+/).filter(Boolean).length;
            const mins = Math.max(1, Math.round(words / 225));
            return mins;
        },
        isTopFull() {
            const p = (this.post?.imagePosition || 'top-full').toLowerCase();
            return p === 'top-full' || p === 'top';
        },
        isBottomFull() {
            const p = (this.post?.imagePosition || '').toLowerCase();
            return p === 'bottom-full' || p === 'bottom';
        },
        isInlineImage() {
            const p = (this.post?.imagePosition || '').toLowerCase();
            return p.startsWith('left-') || p.startsWith('right-');
        },
        inlineFigClass() {
            const p = (this.post?.imagePosition || '').toLowerCase();
            return {
                'news-detail__inline-fig--left':  p.startsWith('left-'),
                'news-detail__inline-fig--right': p.startsWith('right-'),
                'news-detail__inline-fig--top':    p.endsWith('-top'),
                'news-detail__inline-fig--middle': p.endsWith('-middle'),
                'news-detail__inline-fig--bottom': p.endsWith('-bottom')
            };
        },
        bodyClass() {
            return {
                'news-detail__body--with-inline': this.isInlineImage
            };
        },
        shareTwitter() {
            if (!this.post) return '#';
            const url = encodeURIComponent(window.location.href);
            const text = encodeURIComponent(this.post.title);
            return `https://twitter.com/intent/tweet?url=${url}&text=${text}`;
        },
        shareLinkedIn() {
            if (!this.post) return '#';
            const url = encodeURIComponent(window.location.href);
            return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        }
    },
    mounted() {
        // Click-to-zoom on any inline image inside the body.
        this.$nextTick(() => {
            const body = document.querySelector('.news-detail__content');
            if (!body) return;
            body.querySelectorAll('img').forEach(img => {
                img.style.cursor = 'zoom-in';
                img.addEventListener('click', () => {
                    this.lightboxSrc = img.src;
                    this.lightboxOpen = true;
                });
            });
        });
    },
    methods: {
        formatDate(d) {
            if (!d) return '';
            const dt = new Date(d);
            return dt.toLocaleDateString(undefined, {
                year: 'numeric', month: 'long', day: 'numeric'
            });
        },
        tagSlug(tag) {
            return encodeURIComponent(tag.toLowerCase().replace(/\s+/g, '-'));
        }
    }
};
</script>

<style scoped>
.news-detail {
    padding: 32px 0 64px;
}

.news-detail__crumbs {
    font-size: 0.85rem;
    color: #64748b;
    margin-bottom: 18px;
}
.news-detail__crumbs a {
    color: #4F46E5;
    text-decoration: none;
    margin-right: 6px;
}
.news-detail__crumbs span {
    margin-right: 6px;
}
.news-detail__crumbs-current {
    color: #0F172A;
}

.news-detail__header {
    margin-bottom: 24px;
}
.news-detail__header h1 {
    font-size: clamp(1.75rem, 3.5vw, 2.4rem);
    font-weight: 700;
    color: #0F172A;
    line-height: 1.2;
    margin: 0 0 12px;
}
.news-detail__meta {
    font-size: 0.9rem;
    color: #64748b;
}
.news-detail__meta > span + span {
    margin-left: 6px;
}
.news-detail__tags {
    margin-top: 14px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.news-detail__tag {
    font-size: 0.78rem;
    color: #4f46e5;
    background: #eef2ff;
    border-radius: 4px;
    padding: 3px 10px;
    text-decoration: none;
    font-weight: 500;
}

.news-detail__hero {
    margin: 24px auto 32px;
    max-width: 920px;
}
.news-detail__hero img {
    width: 100%;
    height: auto;
    border-radius: 12px;
    display: block;
}
.news-detail__hero figcaption {
    font-size: 0.85rem;
    color: #64748b;
    margin-top: 8px;
    text-align: center;
    font-style: italic;
}
.news-detail__hero--bottom {
    margin: 32px auto 0;
}

.news-detail__body {
    max-width: 760px;
    font-size: 1rem;
    line-height: 1.75;
    color: #1e293b;
}
.news-detail__body--with-inline {
    overflow: hidden;
}

.news-detail__inline-fig {
    margin: 0 0 16px;
    max-width: 320px;
}
.news-detail__inline-fig img {
    width: 100%;
    height: auto;
    border-radius: 8px;
    display: block;
}
.news-detail__inline-fig figcaption {
    font-size: 0.82rem;
    color: #64748b;
    margin-top: 6px;
    font-style: italic;
}
.news-detail__inline-fig--left  { float: left;  margin-right: 24px; }
.news-detail__inline-fig--right { float: right; margin-left: 24px; }
@media (max-width: 600px) {
    .news-detail__inline-fig {
        float: none !important;
        margin: 0 auto 16px !important;
        max-width: 100%;
    }
}

.news-detail__content {
    /* Restyle content authored via QEditor */
}
.news-detail__content :deep(h2) { font-size: 1.5rem; font-weight: 600; color: #0F172A; margin: 32px 0 12px; }
.news-detail__content :deep(h3) { font-size: 1.2rem; font-weight: 600; color: #0F172A; margin: 24px 0 10px; }
.news-detail__content :deep(p)  { margin: 0 0 16px; }
.news-detail__content :deep(ul), .news-detail__content :deep(ol) { margin: 0 0 16px; padding-left: 24px; }
.news-detail__content :deep(li) { margin-bottom: 6px; }
.news-detail__content :deep(blockquote) {
    border-left: 4px solid #4F46E5;
    padding: 8px 18px;
    margin: 16px 0;
    background: #f8fafc;
    color: #475569;
    font-style: italic;
}
.news-detail__content :deep(a) { color: #4F46E5; text-decoration: none; }
.news-detail__content :deep(a:hover) { text-decoration: underline; }
.news-detail__content :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 16px 0;
}
.news-detail__content :deep(code) {
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.9em;
    color: #be185d;
}
.news-detail__content :deep(pre) {
    background: #0F172A;
    color: #e2e8f0;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;
}
.news-detail__content :deep(pre code) {
    background: transparent;
    padding: 0;
    color: inherit;
}

.news-detail__actions {
    max-width: 760px;
    margin-top: 40px;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.news-detail__back {
    color: #4F46E5;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.95rem;
}
.news-detail__share a {
    color: #64748b;
    margin-left: 12px;
    text-decoration: none;
}
.news-detail__share a:hover {
    color: #4F46E5;
}
.news-detail__share-icon {
    /* Inline-flex so the inline SVG sits on the text baseline cleanly the
       same way the old <q-icon> did. fill=currentColor on the SVGs picks up
       the anchor color + hover transition above. */
    display: inline-flex;
    align-items: center;
}

.news-lightbox {
    position: relative;
    background: #000;
    max-width: 90vw;
    max-height: 90vh;
}
.news-lightbox img {
    width: 100%;
    height: auto;
    max-height: 90vh;
    object-fit: contain;
    display: block;
}
.news-lightbox__close {
    position: absolute;
    top: 8px;
    right: 8px;
    color: #fff;
    background: rgba(0, 0, 0, 0.4);
}

.inline-block {
    display: inline-block;
}
</style>
